import { useEffect, useState, type ReactNode } from 'react';
import { tv } from 'tailwind-variants';
import { getProductsVisualFeatureFlags } from '../../config/featureFlags.js';
import { DetailDrawer } from './DetailDrawer.js';

const LG_MIN_WIDTH_MQ = '(min-width: 1024px)';

const root = tv({
  base: 'flex min-h-0 w-full flex-1 flex-col',
  variants: {
    docked: {
      true: [
        'lg:grid lg:min-h-[min(70vh,calc(100dvh-12rem))] lg:gap-0',
        /* AC-MD-01 / §7.1: detail column 360–420px (lg may cap at 45%) */
        'lg:grid-cols-[minmax(0,1fr)_minmax(360px,min(420px,45%))]',
        'xl:grid-cols-[minmax(0,1fr)_minmax(360px,420px)]',
      ].join(' '),
      false: '',
    },
  },
  defaultVariants: {
    docked: false,
  },
});

/** AC-MD-09: master scrolls independently of detail. */
const masterPane = tv({
  base: 'admin-scrollbar min-h-0 min-w-0 overflow-y-auto',
});

/**
 * Docked detail column — no backdrop / modal overlay (AC-MD-02).
 * Overflow hidden so `CatalogDetailPanelShell` owns body scroll (AC-MD-09).
 */
const detailDock = tv({
  base: [
    'flex min-h-0 flex-col overflow-hidden',
    'border-l border-[var(--color-an-border)]',
    'bg-[var(--color-an-surface,white)]',
  ].join(' '),
});

function useIsLgViewport(): boolean {
  const [isLg, setIsLg] = useState((): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(LG_MIN_WIDTH_MQ).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia(LG_MIN_WIDTH_MQ);
    const onChange = (): void => {
      setIsLg(mq.matches);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, []);

  return isLg;
}

export interface CatalogMasterDetailLayoutProps {
  readonly master: ReactNode;
  /**
   * Docked: wrap in `CatalogDetailPanelShell` (shell owns header/footer).
   * Undocked (`<lg` / split off): body-only — `DetailDrawer` owns chrome via
   * `detailTitle` / `detailSubtitle` / `detailFooter`.
   */
  readonly detail: ReactNode | null;
  readonly detailOpen: boolean;
  readonly onDetailClose: () => void;
  /**
   * @deprecated Prefer omitting — empty selection no longer reserves a docked column
   * (list stays full-width until a row is selected). Kept for call-site compatibility.
   */
  readonly emptyDetail?: ReactNode;
  /** Mobile `DetailDrawer` chrome (AC-MD-05). */
  readonly detailTitle?: string;
  readonly detailSubtitle?: string;
  readonly detailFooter?: ReactNode;
  /**
   * Overrides `getProductsVisualFeatureFlags().splitPaneEnabled` (AC-MD-07).
   * When false, always uses overlay drawer (even on lg+).
   */
  readonly splitPaneEnabled?: boolean;
  /**
   * @deprecated Ignored — undocked detail always uses `DetailDrawer` (Wave-1
   * Catalog self-chrome removed). Pass body-only `detail` + `detailTitle` /
   * `detailSubtitle` / `detailFooter`; do not wrap undocked children in
   * `CatalogDetailPanelShell`.
   */
  readonly detailProvidesChrome?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

/**
 * Catalog master–detail shell (GAP-19 / AC-MD-01..02,05,07,09).
 * Desktop lg+: docked split only while a selection is open (no empty placeholder
 * column). Below lg (or flag off): `DetailDrawer` overlay (AC-MD-05).
 */
export function CatalogMasterDetailLayout({
  master,
  detail,
  detailOpen,
  onDetailClose,
  // emptyDetail is intentionally ignored — list stays full-width until a row is selected
  detailTitle = '',
  detailSubtitle,
  detailFooter,
  splitPaneEnabled: splitPaneOverride,
  // detailProvidesChrome retained for call-site compat; undocked always DetailDrawer
  className,
  testId = 'catalog-master-detail-layout',
}: CatalogMasterDetailLayoutProps): JSX.Element {
  const flagEnabled =
    splitPaneOverride ?? getProductsVisualFeatureFlags().splitPaneEnabled;
  const isLg = useIsLgViewport();
  const canDock = flagEnabled && isLg;
  /** Split column only when there is a real selection — avoid empty pane overlap. */
  const detailColumnOpen = canDock && detailOpen && detail != null;
  const drawerOpen = !canDock && detailOpen && detail != null;

  return (
    <div
      className={root({ docked: detailColumnOpen, className })}
      data-testid={testId}
      data-layout={canDock ? 'split' : 'drawer'}
      data-presentation="panel"
      data-split-pane={flagEnabled ? 'on' : 'off'}
      data-detail-column={detailColumnOpen ? 'open' : 'closed'}
      data-no-backdrop={detailColumnOpen ? 'true' : undefined}
    >
      <div className={masterPane()} data-testid="catalog-master-pane">
        {master}
      </div>

      {detailColumnOpen ? (
        <aside
          className={detailDock()}
          data-testid="catalog-detail-pane"
          data-docked="true"
          data-no-backdrop="true"
          aria-label={detailTitle || undefined}
        >
          <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
            {detail}
          </div>
        </aside>
      ) : null}

      {drawerOpen ? (
        <DetailDrawer
          open
          title={detailTitle}
          subtitle={detailSubtitle}
          onClose={onDetailClose}
          footer={detailFooter}
        >
          {detail}
        </DetailDrawer>
      ) : null}
    </div>
  );
}
